import type { Paths } from './interfaces/paths';
import { BRAND } from './constants';
class PathsManifest {
  basePath: string;
  brand: string;
  returnAbsolutePaths: boolean;
  host: string;
  paths: { [key: string]: string };

  constructor(basePath: string, brand: string, returnAbsolutePaths = false, host = 'www.zillow.com') {
    this.basePath = basePath;
    this.brand = brand;
    this.returnAbsolutePaths = returnAbsolutePaths;
    this.host = host;
    this.paths = {};
  }

  concatParams = (queryString: string | null = null, url: string, encode = false) => {
    const returnAbsolutePaths = this.returnAbsolutePaths;

    let path = '';

    if (!queryString) {
      path = url.split('?')[0];
    } else if (encode) {
      path = url + encodeURIComponent(queryString);
    } else {
      path = url + queryString;
    }

    if (returnAbsolutePaths) {
      path = String(this.getAbsolutePath(path));
    }

    return path;
  };

  formatUrl = (url = '') => {
    const basePath = this.basePath;
    const returnAbsolutePaths = this.returnAbsolutePaths;

    let path = '';

    if (url) {
      path = `${basePath}/${url}`;
    } else {
      path = basePath;
    }

    if (returnAbsolutePaths) {
      path = String(this.getAbsolutePath(path));
    }

    return path;
  };

  getAbsolutePath = (path: string) => {
    if (typeof path !== 'string') {
      return path;
    }

    if (path.indexOf('http') !== -1 || path.indexOf('www') !== -1) {
      // already has host
      return path;
    }

    if (this.host.indexOf('localhost') !== -1) {
      const localhostProtocol = typeof window !== 'undefined' ? window.location.protocol : 'https:';
      return `${localhostProtocol}//${this.host}${path}`;
    }

    const absoluteRootPath = `https://${this.host}`;

    return `${absoluteRootPath}${path}`;
  };

  getAllPaths = (): Paths => {
    return {
      // actions
      logoutAction: '/proxy/rental-manager-api/api/web/v2/users/get',

      // proxy
      proxy: this.formatUrl('proxy'),

      // base
      base: this.formatUrl(),

      // pages
      adManagementPage: this.formatUrl('ad-management'),
      analyticsPage: this.formatUrl('analytics'),
      billingPage: this.formatUrl('billing'),
      details: this.formatUrl('details'),
      editListingPage: this.formatUrl('user/edit-listing'),
      featureFlagPage: this.formatUrl('featureFlags'),
      frontPage: this.formatUrl(),
      homePage: '/',
      inboxPage: this.formatUrl('inbox'),
      inquiryContactPage: this.formatUrl('inquiry-contact'),
      leadManagementPage: this.formatUrl('lead-management'),
      leadPage: this.formatUrl('lead'),
      leadsPage: this.formatUrl('leads'),
      myListingsPage: this.formatUrl('my-listings'),
      postAListingPage: this.formatUrl('post-a-listing'),
      propertiesPage: this.formatUrl('properties'),
      rentGuaranteePage: this.formatUrl('rental-protection'),
      screening: this.formatUrl('screening'),
      selectPropertyPage: this.formatUrl('select-property'),
      settingsPage: this.formatUrl('settings'),
      statisticsPage: this.formatUrl('statistics'),
      submitRequest: this.formatUrl('submit-request'),
      termsPage: this.formatUrl('terms'),
      twoFactorAuthPage: this.formatUrl('2fa'),
      userManagementPage: this.formatUrl('user-management'),
      maintenanceTrackerPage: this.formatUrl('maintenance-tasks'),
      paymentsDashboardPage: this.formatUrl('payments/properties'),
      paymentsAggregatePage: this.formatUrl('rent-payment/overview'),
      paymentsGetStartedPage: this.formatUrl('rent-payment/get-started'),
      rentPaymentPage: this.formatUrl('rent-payment'),

      // resource article on pricing a rental
      priceRentalArticle: this.formatUrl('resources/3-tips-monthly-rent-price/rental-manager'),
      properties: 'properties',
      analytics: 'analytics',
      leads: 'leads',
      billing: 'billing',
      userManagement: 'user-management',
      // leasing
      leasing: this.formatUrl('leasing'),
      globalLeadsPage: 'leads',

      // analytics details sub routes
      analyticsOverview: 'overview',
      analyticsListingPerformance: 'listing-performance',
      analyticsAdSpend: 'adspend',
      analyticsAudience: 'audience',
      analyticsCompetition: 'competition',
      analyticsRentComps: 'rentcomps',
      analyticsMarketInsights: 'market-insights',
      analyticsMarketReports: 'market-reports',

      // Ad preview page for MFH partners
      adPreview: 'preview',
      boostPreview: 'boost-preview',

      // ad management sub routes
      setupTasks: 'setup-tasks',
      adManagementTabsPage: 'self-serve-data/sales-pro',
      adManagementNonAdvertiserOlympus: 'educate-olympus',

      // billing sub routes
      billingDue: 'due',
      billingPaid: 'paid',
      invoicePage: 'invoice',
      paymentSettings: 'paymentSettings',

      // user management sub routes
      userManagementUsers: 'users',
      userManagementPending: 'pending',
      userManagementActivity: 'activity',
      userManagementAddUser: 'users/add',
      userManagementEditUser: 'users/edit',
      userManagementUnrecognizedUsers: 'unrecognized-users',
      userManagementApplicationPermissions: 'application-permissions',

      // listing teardown page
      listingTeardownPage: this.formatUrl('deactivate'),

      // property details sub routes
      propertyDetailsListing: 'listing',
      propertyDetailsApplications: 'applications',
      propertyDetailsPayments: 'payments',
      propertyDetailsContacts: 'contacts',
      propertyDetailsAnalytics: 'analytics',
      propertyDetailsReviews: 'reviews',
      propertyDetailsLeases: 'leases',
      propertyDetailsSources: 'sources',
      propertyDetailsRentGuarantee: 'rental-protection',

      // Price rental page
      zillowPriceRentalLandingPage: this.formatUrl('price-my-rental/'),
      zillowPriceRentalResultsPage: this.formatUrl('price-my-rental/results'),

      // Market trends page
      zillowMarketTrendsPage: this.formatUrl('market-trends/'),

      // Email Verification Path
      emailVerification: this.formatUrl('email-verification'),

      // Security Inquiry Path
      securityInquiry: this.formatUrl('security-inquiry'),

      // Id Veirifcation Path
      idVerification: this.formatUrl('id-verification'),

      // Tenant placement path
      tenantPlacement: this.formatUrl('tenant-placement'),

      // Export Excel Paths
      exportExcel: {
        leads: '/users/leads/exportExcel',
        adSpendAll: '/users/analytics/adproducts/exportExcel',
        boostAd: '/users/analytics/cpmBoostStats/exportExcel',
        properties: '/users/analytics/properties/exportExcel',
      },

      // hotpads
      adminPage: '/admin',
      hotpadsGAPage: 'https://hotpads.com/node/rep/ga-analytics',
      experimentPage: 'https://hotpads.com/node/rep/abtests',
      repsPage: '/rep',
      salesPage: '/sales',
      // end hotpads

      // zillow
      fsboPage: 'https://www.zillow.com/for-sale-by-owner/',
      multifamilyAdvertisingPage: 'https://www.zillow.com/z/rental-property-advertising',
      resourcesPage: 'https://www.zillow.com/rental-manager/resources/',
      mfhResourcesPage: 'https://www.zillow.com/multifamily-knowledge-center/',
      tenantScreeningPage: 'https://www.zillow.com/z/rental-manager/tenant-screening/',
      zestimatePage: 'https://www.zillow.com/info/what-is-a-rent-zestimate/',
      impersonatePage: 'https://www.zillow.com/user/Impersonate.htm',
      zillowLogoutPage: 'https://www.zillow.com/Logout.htm',
      corpTermsURL: 'https://www.zillow.com/corp/Terms.htm',
      rentalTermsURL: 'https://www.zillow.com/renter-hub/terms/Rental-User-Terms',
      pledgeURL: 'https://www.zillow.com/corp/respectful-renting-pledge/',
      photosFormat:
        'https://www.zillow.help/article/do-the-photos-need-to-be-a-certain-format-and-size-zd30234452011667',
      listingDeclined: 'https://www.zillow.help/article/why-didnt-my-listing-go-live-zd30234367973523',
      rentPage: 'https://www.zillow.com/rent/',
      // end zillow

      // reg login
      regLogin: {
        signin: '/user/acct/login/',
        join: '/user/acct/register/',
      },

      // zrm subnav links
      zrmHomePage: this.formatUrl(),
      postAListingMarketingPage: this.formatUrl('post-a-listing/'),
      tenantScreeningStargatePage: 'https://www.zillow.com/z/rental-manager/tenant-screening/',
      onlineLeasesPage: 'https://www.zillow.com/z/rental-manager/rental-lease-agreements/',
      rentPaymentsPage: 'https://www.zillow.com/z/rental-manager/collect-rent-online/',
      landlordingBasicsPage: 'https://www.zillow.com/rental-manager/resources/landlording-basics/',
      toolsAndFormsPage: 'https://www.zillow.com/rental-manager/resources/tools-forms/',
      managingYourRentalPage: 'https://www.zillow.com/rental-manager/resources/managing-your-rental/',
      landlordLawsAndRegulationsPage: 'https://www.zillow.com/rental-manager/resources/landlord-laws-regulations/',

      contactSupportPage: 'https://www.zillow.com/rental-manager/submit-request',
      paidInclusion: {
        faq: 'https://www.zillow.help/article/paying-for-your-zillow-rental-manager-listings-zd30237393777555',
      },

      // payments
      paymentTermsURL: 'https://stripe.com/us/connect-account/legal',
      bankAuthorizationTermsURL: 'https://plaid.com/legal',
      rentPayment: 'rent-payment',
      payments: 'payments',

      // property condition
      propertyCondition: this.formatUrl('property-condition'),

      // RMW Mobile Apps
      iOS: 'https://itunes.apple.com/us/app/postlets-post-rentals-free/id759202892?ls=1&mt=8',
      android: 'https://play.google.com/store/apps/details?id=com.zillow.postlet',

      hud: 'http://www.hud.gov/offices/fheo/index.cfm',

      legacyZRM: {
        editListing: this.formatUrl('listing'),
        myListings: this.formatUrl('posts'),
        priceRental: this.formatUrl('rent-comp-estimator'),
        inbox: this.formatUrl('inbox'),
        createListing: this.formatUrl('listing/create'),
        applicationsLandingPage: 'https://www.zillow.com/marketing/landlord/tenant-screening/',
        howItWorksLandingPage: this.formatUrl('how-it-works'),
      },

      marketingRoutes: {
        onboarding: {
          applications: this.formatUrl('properties/applications'),
          payments: this.formatUrl('properties/payments'),
          leases: this.formatUrl('properties/leases'),
        },
        screening: this.formatUrl('tenant-screening'),
        collectRent: this.formatUrl('collect-rent-online'),
        screeningLangingPage: 'https://www.zillow.com/z/rental-manager/tenant-screening/',
        collectRentLandingPage: 'https://www.zillow.com/z/rental-manager/collect-rent-online/',
      },

      /**** iife ****/
      helpPage: (() => {
        const brand = this.brand;

        switch (brand) {
          case BRAND.HOTPADS:
            return 'https://help.hotpads.com/hc/en-us';
          case BRAND.ZILLOW:
            return 'https://www.zillow.help/renter-landlord-property-manager';
          default:
            break;
        }
      })(),

      profilePage: (() => {
        const brand = this.brand;

        switch (brand) {
          case 'hotpads':
            return '/user/edit';
          case 'zillow':
            return '/user/EditProfile.htm';
          default:
            break;
        }
      })(),

      userSettingsPage: (() => {
        const brand = this.brand;

        switch (brand) {
          case 'hotpads':
            return '/user/settings';
          case 'zillow':
            return '/myzillow/account/';
          default:
            break;
        }
      })(),

      logout: (() => {
        const brand = this.brand;

        switch (brand) {
          case 'hotpads':
            return '/';
          case 'zillow':
            return '/rental-manager';
          default:
            break;
        }
      })(),

      postLogout: (() => {
        const brand = this.brand;

        switch (brand) {
          case 'hotpads':
            return '/';
          case 'zillow':
            return '/Logout.htm';
          default:
            break;
        }
      })(),

      stopImpersonatingPage: (() => {
        const brand = this.brand;

        switch (brand) {
          case 'hotpads':
            return 'https://hotpads.com/rep/changeUserId.htm?action=changeUserIdBackToRep';
          case 'zillow':
            return 'https://www.zillow.com/myzillow/Profile,$PageWrapper.$DirectLink.sdirect';
          default:
            break;
        }
      })(),

      /**** functions ****/
      signInPage: (queryString: string) => {
        const brand = this.brand;

        switch (brand) {
          case BRAND.HOTPADS:
            return this.concatParams(queryString, '/login?redirect=', false);
          case BRAND.ZILLOW:
            return this.concatParams(queryString, '/user/acct/login/?url=', true);
          default:
            break;
        }
      },
      registerPage: (queryString: string) => {
        const brand = this.brand;

        switch (brand) {
          case BRAND.ZILLOW:
            return this.concatParams(queryString, '/user/acct/register/?url=', true);
          default:
            break;
        }
      },
      zrmSignInPage: (queryString: string) => {
        const zrmLoginPagePath = '/rental-manager/login/';

        return queryString && queryString.includes(zrmLoginPagePath)
          ? zrmLoginPagePath
          : this.concatParams(queryString, zrmLoginPagePath + '?url=', true);
      },
      ...this.paths,
    };
  };

  addAdditionalPaths = (key: string, path: string, formatPath = false) => {
    this.paths[key] = formatPath ? this.formatUrl(path) : path;
  };
}

export { PathsManifest };
